// src/context/ThemeContext.tsx
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { resolveInitialTheme, type ThemeColor, type ThemeOptions } from './themePreference';
export type { ThemeColor, ThemeMode, ThemeOptions } from './themePreference';

// Interfaccia per il contesto del tema
interface ThemeContextType {
  theme: ThemeOptions;
  setTheme: (theme: Partial<ThemeOptions>) => void;
  toggleMode: () => void;
  setColor: (color: ThemeColor) => void;
}

// Creazione del contesto
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Provider del tema
export function ThemeProvider({ children }: { children: ReactNode }) {
  // Recupera le impostazioni salvate o usa i valori predefiniti
  const [theme, setThemeState] = useState<ThemeOptions>(() => {
    if (typeof window === 'undefined') return resolveInitialTheme(null, false);
    return resolveInitialTheme(
      localStorage.getItem('theme'),
      window.matchMedia('(prefers-color-scheme: dark)').matches,
    );
  });

  // Funzione per aggiornare il tema
  const setTheme = (newTheme: Partial<ThemeOptions>) => {
    setThemeState(prevTheme => {
      const updatedTheme = { ...prevTheme, ...newTheme };
      // Salva nel localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', JSON.stringify(updatedTheme));
      }
      return updatedTheme;
    });
  };

  // Funzione per alternare tra tema chiaro e scuro
  const toggleMode = () => {
    const newMode = theme.mode === 'dark' ? 'light' : 'dark';
    setTheme({ mode: newMode });
  };
  
  // Funzione per impostare il colore primario
  const setColor = (color: ThemeColor) => {
    setTheme({ color });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;
    root.classList.toggle('dark', theme.mode === 'dark');
    root.classList.remove('light');
    root.style.setProperty('--color-primary', `var(--color-${theme.color}-600)`);
    root.style.setProperty('--color-primary-light', `var(--color-${theme.color}-500)`);
    root.style.setProperty('--color-primary-dark', `var(--color-${theme.color}-700)`);
    root.style.setProperty('--ring-color', `var(--color-${theme.color}-500)`);
  }, [theme]);

  const value = {
    theme,
    setTheme,
    toggleMode,
    setColor
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// Hook per utilizzare il tema
export function useTheme() {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme deve essere usato all\'interno di un ThemeProvider');
  }
  
  return context;
}